import { Eye, KeyRound, Lock, LogIn, ShieldAlert, UserCog, UserRound, Users, type LucideIcon } from 'lucide-react';
import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import type { AuditEntry } from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { SearchInput } from '../../../../components/Input';
import { SkeletonLines } from '../../../../components/Skeleton';
import { ToneIcon } from '../../../../components/Tone';
import { formatClockTime, formatZoneName } from '../../../../lib/format';
import { groupByDay, headlineOf, summaryOf, toneOf, type AccessLogLookups } from './access-log-words';
import styles from './AccessLogFeed.module.css';

// USER-06's middle column (Access log A): the search, then the feed under one heading per day. It loads the next
// page as the end comes into view (the server's cursor), with a button for the same, so it reaches every entry.

/** Typing settles for this long before the feed is asked again (06-qol §6). */
const TYPING_SETTLE_MS = 250;

/** Marks an entry the arrow keys move between. */
const ENTRY_ATTRIBUTE = 'data-audit-entry';

interface AccessLogFeedProps {
  readonly entries: readonly AuditEntry[];
  readonly lookups: AccessLogLookups;
  readonly selectedId: string | null;
  readonly onSelect: (entry: AuditEntry) => void;
  /** Beside the story, the arrow keys open each entry they reach; where the story is its own view they only move. */
  readonly isSelectOnMove: boolean;
  readonly q: string | undefined;
  readonly onSearch: (q: string | undefined) => void;
  readonly isFirstLoad: boolean;
  readonly hasMore: boolean;
  readonly isLoadingMore: boolean;
  readonly onLoadMore: () => void;
  readonly whenText: string;
  /** Shown in place of the list: an empty or failed state. */
  readonly emptyState: ReactNode;
}

export function AccessLogFeed({
  entries,
  lookups,
  selectedId,
  onSelect,
  isSelectOnMove,
  q,
  onSearch,
  isFirstLoad,
  hasMore,
  isLoadingMore,
  onLoadMore,
  whenText,
  emptyState,
}: AccessLogFeedProps): ReactNode {
  const groups = groupByDay(entries);
  const zone = formatZoneName(entries[0]?.occurredAt ?? new Date());

  const move = (event: KeyboardEvent<HTMLElement>): void => {
    const target = event.target;
    if (!(target instanceof HTMLElement) || !target.hasAttribute(ENTRY_ATTRIBUTE)) return;
    const items = Array.from(event.currentTarget.querySelectorAll<HTMLElement>(`[${ENTRY_ATTRIBUTE}]`));
    const index = items.indexOf(target);
    let next: number | null = null;
    if (event.key === 'ArrowDown') next = Math.min(items.length - 1, index + 1);
    else if (event.key === 'ArrowUp') next = Math.max(0, index - 1);
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = items.length - 1;
    if (next === null || next === index) return;
    event.preventDefault();
    items[next]?.focus();
    if (!isSelectOnMove) return;
    const id = items[next]?.getAttribute(ENTRY_ATTRIBUTE);
    const entry = entries.find((candidate) => candidate.id === id);
    if (entry) onSelect(entry);
  };

  return (
    <section className={styles.feed} aria-label="The access log">
      <div className={styles.searchBar}>
        <FeedSearch q={q} onSearch={onSearch} />
        <span className={styles.zone}>Times in {zone}</span>
      </div>

      {isFirstLoad ? (
        <div className={styles.skeleton}>
          <SkeletonLines count={10} />
        </div>
      ) : entries.length === 0 ? (
        emptyState
      ) : (
        <div className={styles.days} onKeyDown={move}>
          {groups.map((group) => (
            <section key={group.day} className={styles.day} aria-label={group.label}>
              <h3 className={styles.dayHeading}>{group.label}</h3>
              <ul className={styles.entries}>
                {group.entries.map((entry) => (
                  <li key={entry.id}>
                    <EntryRow entry={entry} lookups={lookups} isSelected={entry.id === selectedId} onSelect={onSelect} />
                  </li>
                ))}
              </ul>
            </section>
          ))}
          {hasMore ? (
            // Keyed by the count, so it looks again after each page: a short page leaves it still in view.
            <MoreSentinel key={entries.length} isLoading={isLoadingMore} onLoadMore={onLoadMore} />
          ) : (
            <p className={styles.end}>That&apos;s everything for {whenText.charAt(0).toLowerCase() + whenText.slice(1)}.</p>
          )}
        </div>
      )}
    </section>
  );
}

function EntryRow({
  entry,
  lookups,
  isSelected,
  onSelect,
}: {
  readonly entry: AuditEntry;
  readonly lookups: AccessLogLookups;
  readonly isSelected: boolean;
  readonly onSelect: (entry: AuditEntry) => void;
}): ReactNode {
  return (
    <button
      type="button"
      className={styles.entry}
      aria-current={isSelected || undefined}
      onClick={() => onSelect(entry)}
      {...{ [ENTRY_ATTRIBUTE]: entry.id }}
    >
      <ToneIcon tone={toneOf(entry)} icon={iconOf(entry)} size="sm" />
      <span className={styles.text}>
        <span className={styles.headline}>{headlineOf(entry)}</span>
        <span className={styles.summary}>{summaryOf(entry, lookups)}</span>
      </span>
      <span className={styles.time}>{formatClockTime(entry.occurredAt)}</span>
    </button>
  );
}

function iconOf(entry: AuditEntry): LucideIcon {
  if (entry.eventType === 'auth.account_locked') return Lock;
  switch (entry.category) {
    case 'sign_in':
      return LogIn;
    case 'failed_sign_in':
      return KeyRound;
    case 'permission_denied':
      return ShieldAlert;
    case 'impersonation':
      return Eye;
    case 'role_change':
      return UserCog;
    case 'delegation':
      return Users;
    default:
      return UserRound;
  }
}

function FeedSearch({ q, onSearch }: { readonly q: string | undefined; readonly onSearch: (q: string | undefined) => void }): ReactNode {
  const [draft, setDraft] = useState(q ?? '');
  const [seen, setSeen] = useState(q);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The address changed some other way (Clear filters, Back): the box follows it.
  if (seen !== q) {
    setSeen(q);
    setDraft(q ?? '');
  }
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  const change = (next: string): void => {
    setDraft(next);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => onSearch(next.trim() === '' ? undefined : next.trim()), TYPING_SETTLE_MS);
  };
  return (
    <SearchInput
      aria-label="Search names and actions"
      placeholder="Search names and actions"
      value={draft}
      onChange={(event) => change(event.target.value)}
      onClear={() => change('')}
      data-primary-search
    />
  );
}

/** The next page loads as this comes into view; the button does the same for a keyboard, or a browser without the observer. */
function MoreSentinel({ isLoading, onLoadMore }: { readonly isLoading: boolean; readonly onLoadMore: () => void }): ReactNode {
  const ref = useRef<HTMLDivElement | null>(null);
  const latest = useRef(onLoadMore);
  useEffect(() => {
    latest.current = onLoadMore;
  });

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver((seen) => {
      if (seen.some((item) => item.isIntersecting)) latest.current();
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={styles.more}>
      <Button variant="quiet" size="sm" isPending={isLoading} onClick={onLoadMore}>
        Show older entries
      </Button>
    </div>
  );
}
